// Doc Order — tab mount root inside the Loan Center slide-out.
// Gates on the DB flag + view_doc_orders permission + Banked-Retail channel,
// then either offers the "Start Doc Order" CTA (create → 2–4s pull behind a
// skeleton, inline retry on a 502) or renders the Workspace for an existing
// order. Everything lives under the [data-doc-order] scope so the ported styles
// apply without leaking into the rest of the portal.
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import type { ProspectLoan } from '../types';
import { Btn, Icon, useToasts } from './atoms';
import { useDocOrder } from './useDocOrder';
import { Workspace } from './Workspace';
import { isDocOrderChannel, DO_REQUIRED_CHANNEL } from './core';
import './styles.css';

function Skeleton() {
  return (
    <div className="do-skeleton" aria-hidden="true">
      <div className="do-sk-bar" style={{ height: 52 }} />
      <div className="do-sk-bar" style={{ height: 180, marginTop: 16 }} />
      <div className="do-sk-bar" style={{ height: 240, marginTop: 16 }} />
    </div>
  );
}

interface DocOrderTabProps {
  loan: ProspectLoan;
}

export function DocOrderTab({ loan }: DocOrderTabProps) {
  const flag = useFeatureFlag('doc_order_v1');
  const { getCurrentUser, hasAnyPermission, isLoadingPermissions } = useRoleAccess();
  const user = getCurrentUser();
  const userName = user?.full_name || user?.email || 'You';
  const canView = hasAnyPermission(['view_doc_orders']);
  const channelOk = isDocOrderChannel(loan.channel);
  const enabled = flag.enabled && channelOk && canView;

  const [addToast, toastNode] = useToasts();
  const docOrder = useDocOrder(loan.id, enabled, addToast);

  let body: React.ReactNode;
  if (flag.loading || isLoadingPermissions) {
    body = (
      <div className="do-center">
        <Icon name="clock" size={22} className="do-center-ico" />
      </div>
    );
  } else if (!flag.enabled || !canView) {
    body = (
      <div className="do-center">
        <Icon name="lock" size={26} className="do-center-ico" />
        <p className="do-center-title">Doc Order isn’t available for your account.</p>
      </div>
    );
  } else if (!channelOk) {
    body = (
      <div className="do-center">
        <Icon name="fileText" size={26} className="do-center-ico" />
        <p className="do-center-title">Doc Order is available on {DO_REQUIRED_CHANNEL} loans only.</p>
        <p className="do-center-sub">This loan’s channel is {loan.channel || 'not set'}.</p>
      </div>
    );
  } else if (docOrder.loading) {
    body = <Skeleton />;
  } else if (docOrder.hasOrder) {
    body = <Workspace docOrder={docOrder} loan={loan} userName={userName} addToast={addToast} />;
  } else if (docOrder.creating) {
    body = <Skeleton />;
  } else if (docOrder.error) {
    body = (
      <div className="do-center">
        <Icon name="alertTriangle" size={26} className="do-center-ico do-center-ico-warn" />
        <p className="do-center-title">{docOrder.error}</p>
        {docOrder.createRetryable && (
          <Btn variant="primary" icon="refresh" onClick={() => void docOrder.createOrder()}>
            Try again
          </Btn>
        )}
        {!docOrder.createRetryable && docOrder.openRetryable && (
          <Btn variant="primary" icon="refresh" onClick={() => void docOrder.reopen()}>
            Try again
          </Btn>
        )}
      </div>
    );
  } else {
    // No order yet — offer to start one (processors / elevated only).
    const canStart = docOrder.viewer?.canEditDraft ?? false;
    body = (
      <div className="do-empty">
        <div className="do-empty-ico">
          <Icon name="fileText" size={30} />
        </div>
        <h3 className="do-empty-title">Start the Doc Order</h3>
        <p className="do-empty-sub">
          Pull this loan’s closing details, third-party fees, and closing documents from Encompass to build the doc order for closing.
        </p>
        {canStart ? (
          <Btn variant="primary" icon="fileText" onClick={() => void docOrder.createOrder()}>
            Start Doc Order
          </Btn>
        ) : (
          <p className="do-center-sub">A processor needs to start the doc order for this loan.</p>
        )}
      </div>
    );
  }

  return (
    <div data-doc-order className="do-root">
      {body}
      {toastNode}
    </div>
  );
}
